/** @jsxImportSource @emotion/react */
import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import moment from 'moment'
import ReactSelect from 'react-select'
import { pick, startCase } from 'lodash'

import { useLabPermission } from 'hooks/useLabPermission'
import { useGetDeviceBrandsQuery, useGetLabDevicesQuery } from 'store/services/devices'
import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import DashboardContent from '../atoms/DashboardContent'
import { UserPermission } from '../user/permissions'
import { handleChange } from '../../atoms/Upsert'
import ButtonNew from '../../atoms/ButtonNew'
import Input from '../../atoms/Input'
import Link from '../../atoms/Link'
import routes from '../constants/routes'
import { PopoverWrapper } from 'components/atoms/PopoverWrapper'
import { keyBy } from 'lodash'
import { useGetLabOfficesQuery } from 'store/services/office'
import Select from 'components/atoms/Select'
import { formatEasternDate } from 'helpers/dates'
import type { DeviceShelfLifeInfo } from 'store/types'
import { HST_CODE_TIER_LABELS } from 'components/lab/payer/hstCodes/hstCodes'
import type { HstCodeTier } from 'ts-back-end/src/business/payer/dto/payer.dto'
import { statusOptions as statusOptionsRaw } from './constants'
import { AccessoriesStateCell } from './AccessoriesStateCell'
import { ExpirationCell } from './ExpirationCell'
import { DEVICE_COMPLAINTS_FILTER_OPTIONS, DEVICE_COMPLAINTS_FILTER_PARAM } from 'constants/deviceComplaints'

const statusOptions = statusOptionsRaw.map((i) => ({ ...i, label: i.key }))
const availabilityOptions = [
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
]
// TES-3191: shelf-life columns, blank for device types whose shelf life is not tracked
type ShelfLifeRow = {
  row: {
    original: {
      firstCheckedOutAt?: string | Date | null
      expirationDate?: string | null
      usedCount?: number
      shelfLife?: DeviceShelfLifeInfo | null
    }
  }
}

// TES-3191: devices past or near the end of their shelf life
const expirationOptions = [
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'EXPIRING_SOON', label: 'Expiring within 30 days' },
]
// where a tracked device stands on its accessories, as the Accessories column shows it; a kind of
// accessory picks the devices whose one of that kind is due for replacement
const accessoriesOptions = [
  { value: 'NOT_ASSIGNED', label: 'Not assigned' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'PROBE', label: 'Expired: Probe' },
  { value: 'PATCH', label: 'Expired: Patch' },
  { value: 'POSITION_SENSOR', label: 'Expired: Position sensor' },
  { value: 'ASSIGNED', label: 'Assigned' },
]
const ACCESSORY_KINDS = ['PROBE', 'PATCH', 'POSITION_SENSOR']
// filters picked from a list rather than typed in
const selectFilters: Record<string, { value: string; label: string }[]> = {
  expiration: expirationOptions,
  accessories: accessoriesOptions,
  [DEVICE_COMPLAINTS_FILTER_PARAM]: DEVICE_COMPLAINTS_FILTER_OPTIONS,
}

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.6rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

const filterOptions = [
  { value: 'type', label: 'Type' },
  { value: 'label', label: 'Label' },
  { value: 'serialBegins', label: 'Serial No. begins with...' },
  { value: 'serialEnds', label: 'Serial No. ends with...' },
  { value: 'mac', label: 'MAC Address' },
  { value: 'state', label: 'Status' },
  { value: 'statusUpdatedAt', label: 'Status Date' },
  { value: 'dme', label: 'Assigned DME' },
  { value: 'owningDme', label: 'Owning DME' },
  { value: 'notes', label: 'Notes' },
  { value: 'availability', label: 'Availability' },
  { value: 'expiration', label: 'Expiration' },
  { value: 'accessories', label: 'Accessories' },
  { value: DEVICE_COMPLAINTS_FILTER_PARAM, label: 'Complaints' },
  // { value: 'createdAt', label: 'Date Added' },
  // { value: 'updated', label: 'Date Updated' },
]

const Devices = () => {
  const navigate = useNavigate()
  const canManageDevices = useLabPermission(UserPermission.MANAGE_DEVICES)
  const { data: brands } = useGetDeviceBrandsQuery({ userType: 'lab' })
  const { data: offices } = useGetLabOfficesQuery({})
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(0)
  const [sortOptions, setSortOptions] = useState({})
  const [values, setValues] = useState({
    column: '',
    search: '',
    brandId: '',
    officeId: '',
    ownedByOfficeId: '',
  })

  const [searchParams] = useSearchParams()
  const complaintsFilter = searchParams.get(DEVICE_COMPLAINTS_FILTER_PARAM)

  useEffect(() => {
    if (complaintsFilter) {
      setValues((prev) => ({ ...prev, column: DEVICE_COMPLAINTS_FILTER_PARAM, search: complaintsFilter }))
      setCurrentPage(0)
    }
  }, [complaintsFilter])

  // a kind of accessory filters on that accessory being due for replacement
  const columnFilter =
    values.column === 'accessories' && ACCESSORY_KINDS.includes(values.search)
      ? { accessoryDue: values.search }
      : { [values.column]: values.search }

  const { data: devices, isLoading } = useGetLabDevicesQuery({
    ...pick(values, ['brandId', 'officeId', 'ownedByOfficeId']),
    ...columnFilter,
    page: currentPage,
    size: pageSize,
    sortField: sortOptions?.id,
    sortOrder: sortOptions?.desc ? 'desc' : 'asc',
  })

  const brandsLookup = useMemo(() => {
    if (!brands?.items) return {}
    return keyBy(brands.items, 'id')
  }, [brands])

  const brandsOptions = useMemo(
    () => brands?.items?.map((brand) => ({ value: brand.id, label: brand.name })) ?? [],
    [brands],
  )

  const officesOptions = useMemo(
    () =>
      offices?.map((o) => ({
        value: o.id,
        label: `${o.officeName} (${o.addressCity}, ${o.addressState})`,
      })),
    [offices],
  )

  const deviceList = useMemo(() => {
    if (!devices?.items) return devices

    return {
      ...devices,
      items: devices.items.map((device) => ({
        ...brandsLookup[device.brandId],
        ...device,
        updatedAt: device.updatedAt?.slice(0, 10),
      })),
    }
  }, [devices, brandsLookup])

  const header = [
    {
      accessor: 'id',
      Header: 'ID',
      // TES-2881: always navigable — the detail page renders read-only without edit permission.
      Cell: ({ row }) => (
        <Link to={`${routes.index}${routes.device.root}${routes.upsert}${row.original.id}`}>
          {row.original.id}
        </Link>
      ),
    },
    {
      accessor: 'brandName',
      Header: 'Type',
      Cell: ({ row }) => (
        <div css={{ position: 'relative' }}>
          <div className="device-type">
            {row.original.name}
            {row.original.hstTier && (
              <div className="text-muted" css={{ fontSize: '0.8rem' }}>
                HST tier: {HST_CODE_TIER_LABELS[row.original.hstTier as HstCodeTier]}
              </div>
            )}
            {canManageDevices && (
              <Link
                className="open-new-tab"
                css={{ display: 'none', position: 'absolute', right: '0', top: 0 }}
                to={`${routes.index}${routes.device.root}${routes.upsert}${row.original.id}`}
                target="_blank"
                icon="fas fa-external-link-alt"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        </div>
      ),
    },
    { accessor: 'serial', key: 'serial', Header: 'Serial' },
    { accessor: 'label', Header: 'Label' },
    { accessor: 'mac', Header: 'MAC Addr.' },
    {
      accessor: 'state',
      key2: 'name',
      Header: 'Status',
      Cell: ({ row }) => startCase(row.original?.state?.toLowerCase().replace(/_/g, ' ')),
    },
    {
      accessor: 'statusUpdatedAt',
      Header: 'Status Date',
      Cell: ({ row }) => {
        if (!row.original.statusUpdatedAt) return null
        return moment(row.original.statusUpdatedAt).format('MM/DD/YYYY')
      },
    },
    {
      accessor: 'availability',
      Header: 'Availability',
      Cell: ({ row }) => {
        if (!row.original.availability) return '-'
        return startCase(row.original.availability.toLowerCase())
      },
    },
    {
      accessor: 'firstCheckedOutAt',
      Header: 'First Check-Out',
      Cell: ({ row }: ShelfLifeRow) =>
        row.original.shelfLife ? formatEasternDate(row.original.firstCheckedOutAt, '—') : '—',
    },
    {
      accessor: 'expirationDate',
      Header: 'Expires',
      Cell: ({ row }: ShelfLifeRow) => <ExpirationCell device={row.original} />,
    },
    {
      accessor: 'usedCount',
      Header: 'Used',
      Cell: ({ row }: ShelfLifeRow) => (row.original.shelfLife ? row.original.usedCount : '—'),
    },
    {
      accessor: 'accessories',
      Header: 'Accessories',
      disableSortBy: true,
      Cell: ({ row }: ShelfLifeRow) => <AccessoriesStateCell shelfLife={row.original.shelfLife} />,
    },
    {
      accessor: 'openComplaintsCount',
      Header: 'Complaints',
      disableSortBy: true,
      Cell: ({ row }) =>
        row.original.openComplaintsCount ? (
          <span className="badge badge-danger">{row.original.openComplaintsCount} open</span>
        ) : (
          '-'
        ),
    },
    { accessor: 'officeName', Header: 'DME' },
    {
      accessor: 'belongsToDme',
      Header: 'DME Owned',
      Cell: ({ row }) => (row.original.belongsToDme ? 'Yes' : '-'),
    },
    {
      accessor: 'notes',
      Header: 'Notes',
      Cell: ({ row }) =>
        row.original.notes && (
          <PopoverWrapper
            placement="left"
            customHeader="Notes"
            customContent={<div className="p-2">{row.original.notes}</div>}>
            <span>
              <i className="fa fa-sticky-note" css={{ color: 'rgb(254,200,0)' }} />
            </span>
          </PopoverWrapper>
        ),
    },
    {
      accessor: 'createdAt',
      Header: 'Date Added',
      Cell: ({ row }) => moment(row.original.createdAt).format('MM/DD/YYYY'),
    },
    {
      accessor: 'updatedAt',
      Header: 'Last Updated',
      Cell: ({ row }) => moment(row.original.updatedAt).format('MM/DD/YYYY'),
    },
  ]

  return (
    <DashboardContent
      title="Devices"
      icon="fa fa-hdd"
      content={
        <div className="card card-primary">
          <form onSubmit={() => {}}>
            <div
              className="card-header row"
              css={{ borderBottom: 'none', position: 'relative', zIndex: '999' }}>
              <div className="col-12 col-sm-1 mb-3 mb-sm-0">
                <Select
                  name="pageSize"
                  className="basic-single w-100 mr-3 form-control"
                  handleChange={(e) => setPageSize(Number(e.target.value))}
                  options={['20', '50', '100'].map((i) => ({ value: i, label: i }))}
                  value={pageSize}
                />
              </div>
              <div className="col-12 col-sm-3 mb-3 mb-sm-0">
                <ReactSelect
                  className="basic-single w-100 mr-3"
                  css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                  classNamePrefix="select"
                  isClearable
                  placeholder="Select Column"
                  aria-label="Select Column"
                  name="hasPayerId"
                  onChange={(item) =>
                    setValues({
                      ...values,
                      column: item?.value ?? '',
                      ...(!item?.value ? { brandId: '', officeId: '', ownedByOfficeId: '' } : {}),
                      search: '',
                    })
                  }
                  options={filterOptions}
                  value={filterOptions.find((o) => o.value === values.column)}
                />
              </div>

              {values.column === 'type' && (
                <div className="col-12 col-sm-3 input-group mb-3">
                  <ReactSelect
                    className="basic-single w-100 mr-3"
                    css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                    classNamePrefix="select"
                    isClearable
                    placeholder="Select Type"
                    name="type"
                    onChange={(item) => {
                      setValues({ ...values, brandId: item?.value ?? '' })
                      setCurrentPage(0)
                    }}
                    options={brandsOptions}
                    value={brandsOptions?.find((brand) => brand.value === values.brandId)}
                  />
                </div>
              )}
              {(values.column === 'dme' || values.column === 'owningDme') && (
                <div className="col-12 col-sm-3 input-group mb-3">
                  <ReactSelect
                    className="basic-single w-100 mr-3"
                    css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select DME Office"
                    name={values.column === 'dme' ? 'officeId' : 'ownedByOfficeId'}
                    onChange={(item) => {
                      setValues({
                        ...values,
                        [values.column === 'dme' ? 'officeId' : 'ownedByOfficeId']:
                          item?.value ?? '',
                      })
                      setCurrentPage(0)
                    }}
                    options={officesOptions}
                    value={
                      officesOptions?.find(
                        (o) =>
                          o.value ==
                          values[values.column === 'dme' ? 'officeId' : 'ownedByOfficeId'],
                      ) ?? null
                    }
                  />
                </div>
              )}
              {values.column === 'state' && (
                <div className="col-12 col-sm-3 input-group mb-3">
                  <ReactSelect
                    className="basic-single w-100 mr-3"
                    css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select Status"
                    name="search"
                    onChange={(item) => {
                      setValues({ ...values, search: item?.value ?? '' })
                      setCurrentPage(0)
                    }}
                    options={statusOptions}
                    value={statusOptions?.find((o) => o.value == values.search) ?? null}
                  />
                </div>
              )}
              {selectFilters[values.column] && (
                <div className="col-12 col-sm-3 input-group mb-3">
                  <ReactSelect
                    className="basic-single w-100 mr-3"
                    css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                    classNamePrefix="select"
                    isClearable
                    placeholder={`Select ${filterOptions.find((o) => o.value === values.column)?.label}`}
                    aria-label={filterOptions.find((o) => o.value === values.column)?.label}
                    name="search"
                    onChange={(item) => {
                      setValues({ ...values, search: item?.value ?? '' })
                      setCurrentPage(0)
                    }}
                    options={selectFilters[values.column]}
                    value={selectFilters[values.column].find((o) => o.value === values.search) ?? null}
                  />
                </div>
              )}
              {values.column === 'availability' && (
                <div className="col-12 col-sm-3 input-group mb-3">
                  <ReactSelect
                    className="basic-single w-100 mr-3"
                    css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select Status"
                    name="search"
                    onChange={(item) => {
                      setValues({ ...values, search: item?.value ?? '' })
                      setCurrentPage(0)
                    }}
                    options={availabilityOptions}
                    value={availabilityOptions?.find((o) => o.value == values.search) ?? null}
                  />
                </div>
              )}
              {!['type', 'dme', 'owningDme', 'state', 'availability'].includes(values.column) && !selectFilters[values.column] && (
                <div className="col-12 col-sm-3 input-group mb-3">
                  <Input
                    autoFocus={true}
                    value={values.search}
                    label={false}
                    placeholder="Search..."
                    name="search"
                    disabled={!values.column}
                    parentDiv={null}
                    handleChange={(event) => {
                      handleChange(event, values, setValues)
                      setCurrentPage(0)
                    }}
                  />{' '}
                  <div className="input-group-append">
                    <span className="input-group-text">
                      <i className="fas fa-search" />
                    </span>
                  </div>
                </div>
              )}

              <div className="col-12 col-sm text-right">
                {canManageDevices && (
                  <>
                    <Link
                      to={`${routes.index}${routes.deviceImport.root}`}
                      label="Bulk Import"
                      icon="fa fa-file-import"
                      className="btn btn-default d-none d-sm-inline-block"
                    />{' '}
                    <ButtonNew to={`${routes.index}${routes.device.root}${routes.upsert}`} />
                  </>
                )}
              </div>
            </div>
            <StyledTableWrapper
              className="card-body row"
              css={{
                '& [role=row]:hover': {
                  '& .open-new-tab': {
                    display: 'block',
                  },
                },
              }}>
              <DataTable
                tableKey={'lab_devices'}
                data={deviceList}
                status={isLoading}
                tableColumns={header}
                pageSize={pageSize}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setSortOptions={setSortOptions}
                entityName="devices"
              />
            </StyledTableWrapper>
          </form>
        </div>
      }
      breadcrumb={[{ label: 'Devices' }]}
    />
  )
}

export default Devices
